/**
 * Learned per-device format ceiling (Paul, Oct 21 2026 — the standard for every network player).
 * Manual Devices limits + fetch evidence live in formatCeilingEvidence.ts. Evidence, never names: when a network renderer is handed a hi-res stream, the play attempt
 * fails without the renderer ever reaching PLAYING (HEOS Amp "Kitchen": fetches 4–6 MB of a
 * 24/96 FLAC, drops the connection, TRANSITIONING → STOPPED), Maestro replays the same track
 * ONCE at the next lower Qobuz tier (27 → 7 → 6). Only when that replay actually plays is the
 * ceiling learned: a failure that is not about format (unreachable, no fetch, the lower tier
 * fails too) is never stored. Later plays start at the ceiling (getFormatIdForRenderer clamps,
 * so SetNext prefetch resolves the NextURI at the same tier — a native Qobuz stream, bit-perfect).
 * The ceiling is re-probed after 30 days or when the device's description fingerprint changes.
 */

import { diagnosticLog } from '../utils/diagnosticLogger';
import { _resetEvidenceForTests, ensureRendererFetchListeners, getManualZoneCap, hasRejectionSignature, hydrateManualZoneCaps, isHiResSource, nextLowerFormatId, rank, rendererFetchedSince, TIMEOUT_MARKERS, type ZoneLimitProfile } from './formatCeilingEvidence';
export { FORMAT_LADDER, isHiResSource, nextLowerFormatId } from './formatCeilingEvidence';
import {
  formatCeilingToast,
  mergeProfileCeilings,
  isFormatCeilingStale,
  loadLocalCeilings,
  persistLearnedCeiling,
  removePersistedCeiling,
  rendererFingerprint,
  saveLocalCeilings,
  type LearnedFormatCeiling,
} from './formatCeilingStore';

export type { LearnedFormatCeiling } from './formatCeilingStore'; import { noteReplayClaimed, replayBlockedReason, resetReplayGuardForTests } from './formatCeilingReplayGuard';
export { formatCeilingLabel, LEARNED_CEILING_KEY, readProfileCeiling } from './formatCeilingStore';
export { MANUAL_LIMIT_KEY, withManualLimitMarker } from './formatCeilingEvidence';
type RendererLike = { id?: string; name?: string; manufacturer?: string } | null | undefined;
// Ceiling lookup (sync; read by getFormatIdForRenderer on every play)

let ceilings: Record<string, LearnedFormatCeiling> = loadLocalCeilings();
/** Applied while the one replay is in flight; dropped unless the replay plays. */
const tentative = new Map<string, number>();

export function _setCeilingsForTests(next: Record<string, LearnedFormatCeiling>): void {
  ceilings = { ...next };
  tentative.clear();
  _resetEvidenceForTests();
  current = null;
  pendingReplay = null;
  resetReplayGuardForTests();
}

/** Zone profiles (Supabase) are the cross-device truth; called after every profile fetch. */
export function hydrateLearnedFormatCeilings(
  zones: Array<ZoneLimitProfile> | null | undefined,
): void {
  if (!Array.isArray(zones)) return;
  hydrateManualZoneCaps(zones); // manual Devices limit always caps (Kitchen, Oct 21)
  ceilings = mergeProfileCeilings(ceilings, zones);
  saveLocalCeilings(ceilings);
}

export function getAllLearnedFormatCeilings(): Record<string, LearnedFormatCeiling> {
  return ceilings;
}

/** Stored ceiling for a device, or null when absent, expired (30 days) or the device changed. */
export function getLearnedFormatCeiling(renderer: RendererLike): LearnedFormatCeiling | null {
  const id = renderer?.id;
  if (!id) return null;
  const entry = ceilings[id];
  if (!entry) return null;
  if (isFormatCeilingStale(entry, rendererFingerprint(renderer))) return null;
  return entry;
}

function effectiveCeilingFormatId(renderer: RendererLike): number | null {
  const id = renderer?.id;
  if (!id) return null;
  const caps = [tentative.get(id) ?? getLearnedFormatCeiling(renderer)?.max_format_id, getManualZoneCap(id)];
  return caps.filter((c): c is number => c != null).sort((a, b) => rank(a) - rank(b))[0] ?? null;
}

/** Clamp a requested Qobuz format id to the device's learned (or in-flight tentative) ceiling. */
export function clampFormatIdToLearnedCeiling(renderer: RendererLike, formatId: number): number {
  const ceiling = effectiveCeilingFormatId(renderer);
  if (ceiling == null || rank(formatId) < 0) return formatId;
  return rank(formatId) > rank(ceiling) ? ceiling : formatId;
}

/** A pre-resolved stream URL is only reusable when its format sits within the ceiling. */
export function formatWithinLearnedCeiling(renderer: RendererLike, formatId: number | undefined | null): boolean {
  if (formatId == null) return true;
  return clampFormatIdToLearnedCeiling(renderer, formatId) === formatId;
}

// Attempt tracking + classification

export type FormatAttempt = {
  renderer: { id: string; name?: string; manufacturer?: string; model?: string };
  trackId: string;
  formatId: number;
  sampleRate: number;
  bitDepth: number;
  replay: () => Promise<void>;
  isReplay: boolean;
  replayClaimed: boolean;
  settled: boolean;
  startedAt: number;
  dispatched: boolean; staleReason?: () => string | null; // sent to the device (not just queued); why a replay is stale
};

type PendingReplay = { rendererId: string; trackId: string; fromFormatId: number; toFormatId: number };

let current: FormatAttempt | null = null;
let pendingReplay: PendingReplay | null = null;

export function beginFormatAttempt(
  renderer: FormatAttempt['renderer'] | null | undefined,
  trackId: string | number,
  formatId: number,
  sampleRate: number,
  bitDepth: number,
  replay: () => Promise<void>, staleReason?: () => string | null,
): FormatAttempt | null {
  if (!renderer?.id) return null;
  ensureRendererFetchListeners();
  const a = { renderer, trackId: String(trackId), formatId, sampleRate: sampleRate || 0, bitDepth: bitDepth || 0, replay };
  const isReplay = !!pendingReplay
    && pendingReplay.rendererId === a.renderer.id
    && pendingReplay.trackId === String(a.trackId);
  if (pendingReplay && !isReplay) dropTentative('superseded');
  current = { ...a, trackId: String(a.trackId), isReplay, replayClaimed: false, settled: false, startedAt: Date.now(), dispatched: false, staleReason };
  return current;
}

export function markFormatAttemptDispatched(trackId: unknown, rendererId?: string): void { // TransportCommand began sending it
  if (current && current.trackId === String(trackId ?? '') && (!rendererId || current.renderer.id === rendererId)) current.dispatched = true;
}

export function cancelFormatCeilingReplay(reason = 'user_navigation'): void { // Next / Previous / a pick: the user wins
  if (current && !current.settled) { current.replayClaimed = true; current.settled = true; } dropTentative(reason);
}

/** True while a lower-tier replay of this track is owed (the failure cooldown must not swallow it). */
export const isFormatCeilingReplayPending = (trackId: unknown): boolean => !!pendingReplay && pendingReplay.trackId === String(trackId ?? '');

export function getCurrentFormatAttempt(): FormatAttempt | null {
  return current;
}

const UNREACHABLE_MARKERS = [
  'not found', 'unreachable', 'no route', 'connection refused', 'econnrefused', 'host is down',
  'proxy_fetch_failed', ...TIMEOUT_MARKERS, 'soap fault', 'http 500',
  'setavtransporturi fail', 'unauthorized', 'session expired', 'sign in',
];

export type FormatCeilingVerdict =
  | { suspect: true; nextFormatId: number; strength: 'connector_signature' | 'start_failure' }
  | { suspect: false; reason: string };

/**
 * Connector 343+ appends `[format_ceiling_suspect …]` when the renderer fetched bytes of a hi-res
 * stream and stopped without PLAYING, and `[no_renderer_fetch]` when it never fetched at all.
 * Older connectors: a start failure after the device accepted the commands is a suspect whose
 * ceiling is only learned if the one lower-tier replay plays.
 */
export function classifyFormatCeilingFailure(
  attempt: FormatAttempt | null,
  errMsg: string | null,
): FormatCeilingVerdict {
  if (!attempt) return { suspect: false, reason: 'no_attempt' };
  if (attempt.isReplay) return { suspect: false, reason: 'replay_already_used' };
  if (attempt.replayClaimed) return { suspect: false, reason: 'replay_claimed' };
  const guard = replayBlockedReason(attempt, errMsg); if (guard) return { suspect: false, reason: guard };
  if (!isHiResSource(attempt.formatId, attempt.sampleRate, attempt.bitDepth)) {
    return { suspect: false, reason: 'not_hi_res' };
  }
  const next = nextLowerFormatId(attempt.formatId, attempt.sampleRate);
  if (next == null) return { suspect: false, reason: 'no_lower_tier' };
  const msg = String(errMsg || '').toLowerCase();
  if (msg.includes('[no_renderer_fetch')) return { suspect: false, reason: 'no_renderer_fetch' };
  if (msg.includes('[format_ceiling_suspect')) {
    return { suspect: true, nextFormatId: next, strength: 'connector_signature' };
  }
  // A timeout after the device fetched audio / play_unconfirmed STOPPED is the hi-res signature.
  const tookAudio = hasRejectionSignature(msg) || rendererFetchedSince(attempt.renderer, attempt.startedAt);
  const blocked = UNREACHABLE_MARKERS.find((m) => msg.includes(m) && !(tookAudio && TIMEOUT_MARKERS.includes(m)));
  if (blocked) return { suspect: false, reason: `not_format:${blocked}` };
  return { suspect: true, nextFormatId: next, strength: 'start_failure' };
}

function dropTentative(reason: string): void {
  if (!pendingReplay) return;
  tentative.delete(pendingReplay.rendererId);
  diagnosticLog('info', 'playback', 'format_ceiling_not_learned', { reason, ...pendingReplay });
  pendingReplay = null;
}

function claimReplay(attempt: FormatAttempt, verdict: Extract<FormatCeilingVerdict, { suspect: true }>): void {
  attempt.replayClaimed = true; noteReplayClaimed(attempt.renderer.id, attempt.trackId);
  pendingReplay = {
    rendererId: attempt.renderer.id,
    trackId: attempt.trackId,
    fromFormatId: attempt.formatId,
    toFormatId: verdict.nextFormatId,
  };
  tentative.set(attempt.renderer.id, verdict.nextFormatId);
  diagnosticLog('info', 'playback', 'format_ceiling_replay', {
    renderer: attempt.renderer.name, rendererId: attempt.renderer.id, trackId: attempt.trackId, strength: verdict.strength,
    from_format_id: attempt.formatId, to_format_id: verdict.nextFormatId, src_rate: attempt.sampleRate, src_bits: attempt.bitDepth,
  });
}

/**
 * QueueController catch path (play_uri threw). Returns true when the one lower-tier replay ran.
 */
export async function replayAtLowerFormatIfCeiling(
  errMsg: string,
  releaseInFlight: () => void,
  ownAttempt?: FormatAttempt | null,
): Promise<boolean> {
  const attempt = ownAttempt ?? current;
  if (attempt && String(errMsg).includes(FORMAT_CEILING_RESTART)) { // queued play stopped above the ceiling
    if (attempt.staleReason?.()) return false; releaseInFlight(); await attempt.replay(); return true; // stale = user moved on / zone changed
  }
  if (attempt?.isReplay) {
    dropTentative('replay_failed');
    return false;
  }
  const verdict = classifyFormatCeilingFailure(attempt, errMsg);
  if (!verdict.suspect || !attempt) {
    if (attempt && !verdict.suspect && verdict.reason !== 'not_hi_res') {
      diagnosticLog('info', 'playback', 'format_ceiling_skipped', { reason: verdict.reason, trackId: attempt.trackId });
    }
    return false;
  }
  claimReplay(attempt, verdict);
  releaseInFlight();
  await attempt.replay();
  return true;
}

export const FORMAT_CEILING_RESTART = 'format_ceiling_restart';

/** A superseded failure still claims the one lower-tier replay, so the newer play goes one tier down. */
export function noteSupersededFormatFailure(attempt: FormatAttempt | null | undefined, errMsg: string): void {
  const verdict = attempt && !attempt.isReplay && !pendingReplay ? classifyFormatCeilingFailure(attempt, errMsg) : null;
  if (attempt && verdict?.suspect) claimReplay(attempt, verdict);
}

/** TransportCommand: a playUri queued before a rejection must not repeat the rejected tier. */
export function assertQueuedPlayWithinCeiling(renderer: RendererLike, formatId: number): void {
  if (renderer?.id && !formatWithinLearnedCeiling(renderer, formatId)) throw new Error(`${FORMAT_CEILING_RESTART}: format ${formatId}`);
}

/** Pending-window expiry path (play_uri returned, renderer never reached PLAYING). */
export function requestFormatCeilingReplay(trackId: string | undefined): boolean {
  const attempt = current;
  if (!attempt || attempt.trackId !== String(trackId ?? '')) return false;
  if (attempt.isReplay) {
    dropTentative('replay_failed');
    return false;
  }
  const verdict = classifyFormatCeilingFailure(attempt, null);
  if (!verdict.suspect) return false;
  claimReplay(attempt, verdict);
  void attempt.replay();
  return true;
}

const SUCCESS_REASONS = new Set([
  'play_uri_ok', 'confirmed_playing', 'poll_playing', 'renderer_playing', 'timeout_verify_playing',
]);
const FAILURE_REASONS = new Set([
  'pending_window_expired', 'confirmation_probe_stopped', 'transport_error', 'daemon_command_error',
]);

/** Hooked into clearPlayUriPending — every play confirmation settles here. */
export function settleFormatAttempt(reason: string): void {
  const attempt = current;
  if (!attempt || attempt.settled) return;
  if (SUCCESS_REASONS.has(reason)) {
    attempt.settled = true;
    if (attempt.isReplay && pendingReplay) {
      commitLearnedCeiling(attempt, pendingReplay);
      return;
    }
    clearCeilingAfterHiResSuccess(attempt);
  } else if (FAILURE_REASONS.has(reason) && attempt.isReplay) {
    attempt.settled = true;
    dropTentative('replay_failed');
  }
}

function commitLearnedCeiling(attempt: FormatAttempt, replay: PendingReplay): void {
  const entry: LearnedFormatCeiling = {
    max_format_id: replay.toFormatId,
    from_format_id: replay.fromFormatId,
    learned_at: new Date().toISOString(),
    fingerprint: rendererFingerprint(attempt.renderer),
    renderer_name: attempt.renderer.name || '',
  };
  ceilings = { ...ceilings, [replay.rendererId]: entry };
  tentative.delete(replay.rendererId);
  pendingReplay = null;
  saveLocalCeilings(ceilings);
  void persistLearnedCeiling(replay.rendererId, attempt.renderer.name || '', entry);
  diagnosticLog('info', 'playback', 'format_ceiling_learned', {
    renderer: attempt.renderer.name, rendererId: replay.rendererId, max_format_id: entry.max_format_id, from_format_id: entry.from_format_id,
  });
  void import('../providers/UIProvider')
    .then(({ showImperativeToast }) => showImperativeToast(formatCeilingToast(attempt.renderer.name, entry.max_format_id), 7000))
    .catch(() => {});
}

/** A re-probe that played above a stale ceiling clears it (fixed firmware regains hi-res). */
function clearCeilingAfterHiResSuccess(attempt: FormatAttempt): void {
  const entry = ceilings[attempt.renderer.id];
  if (!entry || rank(attempt.formatId) <= rank(entry.max_format_id)) return;
  if (!isHiResSource(attempt.formatId, attempt.sampleRate, attempt.bitDepth)) return;
  diagnosticLog('info', 'playback', 'format_ceiling_reprobe_cleared', {
    rendererId: attempt.renderer.id, played_format_id: attempt.formatId, old_max_format_id: entry.max_format_id,
  });
  void resetLearnedFormatCeiling(attempt.renderer.id, attempt.renderer.name || '');
}

/** Devices → "Reset learned limit". */
export async function resetLearnedFormatCeiling(rendererId: string, rendererName = ''): Promise<void> {
  if (!rendererId) return;
  const next = { ...ceilings };
  delete next[rendererId];
  ceilings = next;
  tentative.delete(rendererId);
  saveLocalCeilings(ceilings);
  diagnosticLog('info', 'playback', 'format_ceiling_reset', { rendererId });
  await removePersistedCeiling(rendererId, rendererName);
}
